import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import { useObserver } from 'mobx-react';
import React, { ChangeEvent, memo, useEffect } from 'react';
import { MaterialTreelistContext } from '..';
import OverflowContent from '../../../../components/OverflowContent';
import { Autocomplete, FormikField } from '../../../../components/forms/fields-next';
import FBAutocompleteAsyncState from '../../../FBAutocompleteAsync/FBAutocompleteAsync.state';
import FBAutocompleteAsyncStore from '../../../FBAutocompleteAsync/FBAutocompleteAsync.store';
import { FBAutocompleteAsyncOption } from '../../../FBAutocompleteAsync/FBAutocompleteAsync.types';
import { FB } from '../../../helpers';
import { CELL_EMPTY_VALUE } from '../constants';
import { CustomTreeListCellProps, EditableMaterial, UnitOption } from '../types';
import TreeListCellWrapper from './TreeListCellWrapper';
import { isEditMaterial } from './utils';

const Editor: React.FC<CustomTreeListCellProps> = ({ field = '', isLHRT, isLHRS, dataItem }) => {
  const { values, setFieldValue, getFieldProps } = useFormikContext<EditableMaterial>();
  const { isRedlineActive } = React.useContext(MaterialTreelistContext);

  const handleChange = (_: ChangeEvent<unknown>, value?: string) => {
    setFieldValue(field, value);
  };

  const unitsAsync = FB.useRef<FBAutocompleteAsyncState>(FBAutocompleteAsyncState, {
    optionId: FBAutocompleteAsyncOption.unitMeasures,
  });

  useEffect(() => {
    unitsAsync.load();
  }, [unitsAsync]);

  const units = useObserver(() => {
    const asyncData = FBAutocompleteAsyncStore.data.get(FBAutocompleteAsyncOption.unitMeasures) as Map<
    string,
    UnitOption
    >;
    return asyncData ? Array.from(asyncData.values()).map((item) => item.text) : [];
  });

  const value = values[field] ?? dataItem[field] ?? '';
  const shouldDisable = isLHRS
    ? isRedlineActive
      ? Boolean(values.parentId)
      : !isEmpty(value)
    : isEmpty(units) || (!isLHRT && values.LHRTparameter);

  return (
    <FormikField name={field}>
      <Autocomplete
        {...getFieldProps(field)}
        key={isEmpty(units) ? 'disabled' : 'enabled'}
        blurOnSelect
        options={units}
        onChange={handleChange}
        value={value}
        size="small"
        disabled={shouldDisable}
      />
    </FormikField>
  );
};

const UnitsCellTemplate: React.FC<CustomTreeListCellProps> = (props) => {
  const { field = '', dataItem } = props;
  const isEditMode = isEditMaterial(dataItem);
  const displayValue = isEmpty(dataItem[field]) ? CELL_EMPTY_VALUE : dataItem[field];

  return (
    <TreeListCellWrapper {...props}>
      {isEditMode ? <Editor {...props} /> : <OverflowContent deferred>{displayValue}</OverflowContent>}
    </TreeListCellWrapper>
  );
};

export default memo(UnitsCellTemplate);
